import React from 'react';
import { connect } from 'react-redux';

import { Link } from 'libs/router';

import Entity from 'components/entity/Entity';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import ManagedByTag from 'components/managed-by-tag/ManagedByTag';

import { getDisplayDate } from 'utils/date';
import { getEntityFromCampaign, getEntityFromProfile } from 'utils/view';
import { isBrandsFocusedOrganization, isInfluencersFocusedOrganization } from 'utils/organization';
import { getManagedByBrand } from 'utils/profile';
import { expandDataProps } from 'utils/helper/common';

import type { CampaignOverview, CampaignWithSubcampaignMeta, Data, Organization } from 'types';

type StateProps = {
  currentOrganization: Organization
};

type OwnProps = {
  campaign: CampaignWithSubcampaignMeta,
  overview: CampaignOverview | undefined | null,
  data?: Data,
  hasCampaignLink: boolean
};

type Props = StateProps & OwnProps;

const getCampaignRoute = (currentOrganization: Organization, campaign: CampaignWithSubcampaignMeta): string => {
  if (campaign.subcampaignMeta) {
    return `/campaigns/${String(campaign.id)}/details/${String(campaign.subcampaignMeta && campaign.subcampaignMeta.id)}`;
  }

  return `/campaigns/${String(campaign.id)}/participants/agreed`;
};

const CampaignsTableCampaignCell = ({
  campaign,
  hasCampaignLink,
  currentOrganization,
  data
}: Props) => {
  const campaignEntity = getEntityFromCampaign(campaign);
  const route = getCampaignRoute(currentOrganization, campaign);
  const brandEntity = getEntityFromProfile(campaign.brandProfile);
  const managedByBrand = getManagedByBrand(campaign.brandProfile);

  const startDate = campaign.startDate;
  const endDate = campaign.endDate;

  return (
    <div className="campaigns-table-campaign-cell">
      <Entity>
        <EntityAvatar
          link={hasCampaignLink ? route : '#'}
          entity={campaignEntity}
          bordered
          form="square"
          size="customSize"
          customSize={{
            width: 68,
            height: 68
          }}
        />
      </Entity>
      {
        isBrandsFocusedOrganization(currentOrganization) && (
          <div className="campaigns-table-campaign-cell__campaign-info" data-hj-suppress>
            <Link to={route}>
              <div
                className="campaigns-table-campaign-cell__campaign-title"
                title={campaignEntity?.displayName}
                {...expandDataProps(data)}
              >
                {campaignEntity?.displayName}
              </div>
            </Link>
            <div className="campaigns-table-campaign-cell__campaign-date">
              {`${getDisplayDate(startDate)} - ${getDisplayDate(endDate)}`}
            </div>
          </div>
        )
      }
      {
        isInfluencersFocusedOrganization(currentOrganization) && (
          <div className="campaigns-table-campaign-cell__campaign-info" data-hj-suppress>
            <div className="campaigns-table-campaign-cell__campaign-info-row">
              <Link to={(hasCampaignLink) ? route : ''}>
                <div
                  className="campaigns-table-campaign-cell__campaign-title"
                  title={campaignEntity?.displayName}
                  {...expandDataProps(data)}
                >
                  {campaignEntity?.displayName}
                </div>
              </Link>
            </div>
            <div className="campaigns-table-campaign-cell__campaign-info-row">
              {
                brandEntity &&
                  <Link to={`/brand/profiles/${brandEntity.id}`}>
                    <div
                      className="campaigns-table-campaign-cell__campaign-brand text-overflow-ellipsis"
                      title={brandEntity.displayName}
                    >
                      {brandEntity.displayName}
                    </div>
                  </Link>
              }
              <div className="campaigns-table-campaign-cell__campaign-info-row__child text-overflow-ellipsis">
                {managedByBrand ?
                  <ManagedByTag
                    className="campaigns-table-campaign-cell__tag-name"
                    managedByBrand={managedByBrand}
                  />
                  : null}
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

const mapStateToProps = ({ organizations }): StateProps => ({
  currentOrganization: organizations.currentOrganization
});

export default connect(mapStateToProps)(CampaignsTableCampaignCell);
